import { WebMcpActor, WebMcpProvenanceArgs, WebMcpProvenanceLog } from './provenance';

export interface RemoveStructureProposalArgs extends WebMcpProvenanceArgs {
    readonly action: 'remove-structure'
    readonly structureRef: string
}

export interface RemoveComponentProposalArgs extends WebMcpProvenanceArgs {
    readonly action: 'remove-component'
    readonly componentRef: string
}

export interface RemoveRepresentationProposalArgs extends WebMcpProvenanceArgs {
    readonly action: 'remove-representation'
    readonly representationRef: string
}

export interface ImportSessionProposalArgs extends WebMcpProvenanceArgs {
    readonly action: 'import-session'
    readonly sessionUrl: string
}

export interface RevertToProposalArgs extends WebMcpProvenanceArgs {
    readonly action: 'revert-to'
    readonly snapshotId: string
}

export interface ClearUserAnnotationsProposalArgs extends WebMcpProvenanceArgs {
    readonly action: 'clear-user-annotations'
}

export interface ResetWorkspaceProposalArgs extends WebMcpProvenanceArgs {
    readonly action: 'reset-workspace'
}

export type DestructiveProposalArgs =
    | RemoveStructureProposalArgs
    | RemoveComponentProposalArgs
    | RemoveRepresentationProposalArgs
    | ImportSessionProposalArgs
    | RevertToProposalArgs
    | ClearUserAnnotationsProposalArgs
    | ResetWorkspaceProposalArgs;

export type ProposalStatus = 'pending' | 'accepted' | 'rejected' | 'failed';

export interface WebMcpProposal {
    readonly id: string
    readonly createdAt: number
    readonly actor: WebMcpActor
    readonly tool: DestructiveProposalArgs['action']
    readonly args: DestructiveProposalArgs
    readonly summary: string
    readonly status: ProposalStatus
    readonly error?: string
}

export interface ProposalExecution {
    readonly summary: string
}

export type ProposalValidation =
    | { readonly ok: true }
    | { readonly ok: false, readonly error: string, readonly hint: string };

export interface RemoveStructureProposalExecutor {
    readonly action: 'remove-structure'
    revalidate(args: RemoveStructureProposalArgs): Promise<ProposalValidation>
    execute(args: RemoveStructureProposalArgs): Promise<ProposalExecution>
}

export interface RemoveComponentProposalExecutor {
    readonly action: 'remove-component'
    revalidate(args: RemoveComponentProposalArgs): Promise<ProposalValidation>
    execute(args: RemoveComponentProposalArgs): Promise<ProposalExecution>
}

export interface RemoveRepresentationProposalExecutor {
    readonly action: 'remove-representation'
    revalidate(args: RemoveRepresentationProposalArgs): Promise<ProposalValidation>
    execute(args: RemoveRepresentationProposalArgs): Promise<ProposalExecution>
}

export interface ImportSessionProposalExecutor {
    readonly action: 'import-session'
    revalidate(args: ImportSessionProposalArgs): Promise<ProposalValidation>
    execute(args: ImportSessionProposalArgs): Promise<ProposalExecution>
}

export interface RevertToProposalExecutor {
    readonly action: 'revert-to'
    revalidate(args: RevertToProposalArgs): Promise<ProposalValidation>
    execute(args: RevertToProposalArgs): Promise<ProposalExecution>
}

export interface ClearUserAnnotationsProposalExecutor {
    readonly action: 'clear-user-annotations'
    revalidate(args: ClearUserAnnotationsProposalArgs): Promise<ProposalValidation>
    execute(args: ClearUserAnnotationsProposalArgs): Promise<ProposalExecution>
}

export interface ResetWorkspaceProposalExecutor {
    readonly action: 'reset-workspace'
    revalidate(args: ResetWorkspaceProposalArgs): Promise<ProposalValidation>
    execute(args: ResetWorkspaceProposalArgs): Promise<ProposalExecution>
}

export type ProposalExecutor =
    | RemoveStructureProposalExecutor
    | RemoveComponentProposalExecutor
    | RemoveRepresentationProposalExecutor
    | ImportSessionProposalExecutor
    | RevertToProposalExecutor
    | ClearUserAnnotationsProposalExecutor
    | ResetWorkspaceProposalExecutor;

export interface ProposalSnapshotStore {
    captureBeforeChange(): void
}

export type ProposalApplyResult =
    | { readonly ok: true, readonly proposal: WebMcpProposal }
    | { readonly ok: false, readonly error: string, readonly hint: string };

export type WebMcpProposalListener = () => void;

/** Stores serialized destructive requests until a caller explicitly applies them. */
export class WebMcpProposalStore {
    private readonly proposals: WebMcpProposal[] = [];
    private readonly listeners = new Set<WebMcpProposalListener>();
    private removeStructureExecutor: RemoveStructureProposalExecutor | undefined;
    private removeComponentExecutor: RemoveComponentProposalExecutor | undefined;
    private removeRepresentationExecutor: RemoveRepresentationProposalExecutor | undefined;
    private importSessionExecutor: ImportSessionProposalExecutor | undefined;
    private revertToExecutor: RevertToProposalExecutor | undefined;
    private clearUserAnnotationsExecutor: ClearUserAnnotationsProposalExecutor | undefined;
    private resetWorkspaceExecutor: ResetWorkspaceProposalExecutor | undefined;
    private nextId = 1;

    constructor(private readonly snapshots: ProposalSnapshotStore, private readonly provenance: WebMcpProvenanceLog) {}

    subscribe(listener: WebMcpProposalListener): () => void {
        this.listeners.add(listener);
        return () => { this.listeners.delete(listener); };
    }

    registerExecutor(executor: ProposalExecutor) {
        switch (executor.action) {
            case 'remove-structure': this.removeStructureExecutor = executor; break;
            case 'remove-component': this.removeComponentExecutor = executor; break;
            case 'remove-representation': this.removeRepresentationExecutor = executor; break;
            case 'import-session': this.importSessionExecutor = executor; break;
            case 'revert-to': this.revertToExecutor = executor; break;
            case 'clear-user-annotations': this.clearUserAnnotationsExecutor = executor; break;
            case 'reset-workspace': this.resetWorkspaceExecutor = executor; break;
        }
    }

    propose(actor: WebMcpActor, args: DestructiveProposalArgs, summary: string, createdAt = Date.now()): WebMcpProposal {
        const proposal: WebMcpProposal = { id: `proposal-${this.nextId++}`, createdAt, actor, tool: args.action, args, summary, status: 'pending' };
        this.proposals.unshift(proposal);
        this.notify();
        return proposal;
    }

    getPending(): readonly WebMcpProposal[] {
        return this.proposals.filter(proposal => proposal.status === 'pending');
    }

    reject(id: string): ProposalApplyResult {
        const proposal = this.findPending(id);
        if (!proposal) return this.notPending();
        return { ok: true, proposal: this.replace(proposal, { status: 'rejected' }) };
    }

    async apply(id: string): Promise<ProposalApplyResult> {
        const proposal = this.findPending(id);
        if (!proposal) return this.notPending();
        return this.executeProposal(proposal);
    }

    private executeProposal(proposal: WebMcpProposal): Promise<ProposalApplyResult> {
        switch (proposal.args.action) {
            case 'remove-structure': return this.withExecutor(proposal, proposal.args, this.removeStructureExecutor);
            case 'remove-component': return this.withExecutor(proposal, proposal.args, this.removeComponentExecutor);
            case 'remove-representation': return this.withExecutor(proposal, proposal.args, this.removeRepresentationExecutor);
            case 'import-session': return this.withExecutor(proposal, proposal.args, this.importSessionExecutor);
            case 'revert-to': return this.withExecutor(proposal, proposal.args, this.revertToExecutor);
            case 'clear-user-annotations': return this.withExecutor(proposal, proposal.args, this.clearUserAnnotationsExecutor);
            case 'reset-workspace': return this.withExecutor(proposal, proposal.args, this.resetWorkspaceExecutor);
        }
    }

    private async withExecutor<Args extends DestructiveProposalArgs>(proposal: WebMcpProposal, args: Args, executor: { revalidate(args: Args): Promise<ProposalValidation>, execute(args: Args): Promise<ProposalExecution> } | undefined): Promise<ProposalApplyResult> {
        if (!executor) return { ok: false, error: `No executor is registered for ${proposal.tool}.`, hint: 'Register the action executor before applying this proposal.' };
        try {
            const validation = await executor.revalidate(args);
            if (!validation.ok) {
                this.replace(proposal, { status: 'failed', error: validation.error });
                return validation;
            }
            this.snapshots.captureBeforeChange();
            const execution = await executor.execute(args);
            const accepted = this.replace(proposal, { status: 'accepted', summary: execution.summary });
            this.provenance.add(proposal.actor, proposal.tool, proposal.args, execution.summary);
            return { ok: true, proposal: accepted };
        } catch (error) {
            const failed = this.replace(proposal, { status: 'failed', error: error instanceof Error ? error.message : 'Proposal execution failed.' });
            return { ok: true, proposal: failed };
        }
    }

    private findPending(id: string): WebMcpProposal | undefined {
        return this.proposals.find(proposal => proposal.id === id && proposal.status === 'pending');
    }

    private notPending(): ProposalApplyResult {
        return { ok: false, error: 'Proposal is not pending.', hint: 'Use get-pending-proposals to choose a pending proposal id.' };
    }

    private replace(proposal: WebMcpProposal, update: Pick<WebMcpProposal, 'status'> & Partial<Pick<WebMcpProposal, 'summary' | 'error'>>): WebMcpProposal {
        const next: WebMcpProposal = { ...proposal, ...update };
        this.proposals[this.proposals.indexOf(proposal)] = next;
        this.notify();
        return next;
    }

    private notify() {
        for (const listener of this.listeners) listener();
    }
}
